import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link as RouterLink, Navigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Chip,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Divider,
  Link,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  CheckCircleOutline,
  OpenInNew,
  ArrowForward,
  CompareArrows,
  AccessTime,
} from '@mui/icons-material';
import SEOHead from '../components/SEO/SEOHead';
import PageHeader from '../components/Layout/PageHeader';
import AtomicAnswer from '../components/SEO/AtomicAnswer';
import FAQSection from '../components/SEO/FAQSection';
import ImpactBadge from '../components/Financlopedia/ImpactBadge';
import ShareButtons from '../components/shared/ShareButtons';
import FeedbackWidget from '../components/shared/FeedbackWidget';
import { financlopediaBySlug, CLUSTERS, clusterBySlug, clusterById } from '../data/financlopediaData';
import { autoLinkTerms } from '../utils/autoLink';
import calculatorRegistry from '../components/FinancialTools/InlineCalculators/calculatorRegistry';
import CalculatorShell from '../components/FinancialTools/InlineCalculators/CalculatorShell';

const SITE_URL = 'https://www.finmaple.com';

const UI = {
  es: {
    categories: { ahorro: 'Ahorro', credito: 'Crédito', impuestos: 'Impuestos', inversion: 'Inversión', vivienda: 'Vivienda', vida: 'Vida financiera' },
    difficulty: { basico: 'Básico', intermedio: 'Intermedio', avanzado: 'Avanzado' },
    breadcrumb: 'Diccionario Financiero',
    readTime: 'min de lectura',
    keyTakeaways: 'Puntos clave',
    toc: 'En este artículo',
    relatedTerms: 'Términos relacionados',
    clusterPrefix: 'Otros términos en',
    learningPath: 'Ruta recomendada',
    learningPathSub: (term) => `Después de ${term}, te recomendamos:`,
    faqTitle: (term) => `Preguntas frecuentes sobre ${term}`,
    sources: 'Fuentes oficiales',
    disclaimer: 'La información de esta página es educativa y no constituye asesoramiento financiero. Consulta fuentes oficiales como canada.ca y las instituciones financieras para datos actualizados.',
  },
  en: {
    categories: { ahorro: 'Savings', credito: 'Credit', impuestos: 'Taxes', inversion: 'Investment', vivienda: 'Housing', vida: 'Financial life' },
    difficulty: { basico: 'Basic', intermedio: 'Intermediate', avanzado: 'Advanced' },
    breadcrumb: 'Financial Dictionary',
    readTime: 'min read',
    keyTakeaways: 'Key takeaways',
    toc: 'In this article',
    relatedTerms: 'Related terms',
    clusterPrefix: 'More terms in',
    learningPath: 'Recommended path',
    learningPathSub: (term) => `After ${term}, we recommend:`,
    faqTitle: (term) => `Frequently asked questions about ${term}`,
    sources: 'Official sources',
    disclaimer: 'The information on this page is educational and does not constitute financial advice. Consult official sources like canada.ca and financial institutions for up-to-date data.',
  },
  fr: {
    categories: { ahorro: 'Épargne', credito: 'Crédit', impuestos: 'Impôts', inversion: 'Investissement', vivienda: 'Logement', vida: 'Vie financière' },
    difficulty: { basico: 'Débutant', intermedio: 'Intermédiaire', avanzado: 'Avancé' },
    breadcrumb: 'Dictionnaire financier',
    readTime: 'min de lecture',
    keyTakeaways: 'Points clés',
    toc: 'Dans cet article',
    relatedTerms: 'Termes connexes',
    clusterPrefix: 'Autres termes dans',
    learningPath: 'Parcours recommandé',
    learningPathSub: (term) => `Après ${term}, nous recommandons\u00a0:`,
    faqTitle: (term) => `Questions fréquentes sur ${term}`,
    sources: 'Sources officielles',
    disclaimer: 'Les informations de cette page sont éducatives et ne constituent pas un conseil financier. Consultez les sources officielles comme canada.ca et les institutions financières pour des données à jour.',
  },
};

const FinanclopediaEntryPage = () => {
  const { i18n } = useTranslation();
  const { slug, lng } = useParams();
  const l = UI[lng] || UI.es;
  const entry = financlopediaBySlug[slug];
  const [takeawaysOpen, setTakeawaysOpen] = useState(true);

  const relatedEntries = useMemo(() => {
    if (!entry) return [];
    return entry.relatedTerms
      .map((s) => financlopediaBySlug[s])
      .filter(Boolean);
  }, [entry]);

  // Cluster navigation
  const currentCluster = useMemo(() => {
    if (!entry) return null;
    const clusterId = clusterBySlug[entry.slug];
    return clusterId ? clusterById[clusterId] : null;
  }, [entry]);

  const clusterSiblings = useMemo(() => {
    if (!currentCluster || !entry) return [];
    return currentCluster.slugs
      .filter((s) => s !== entry.slug)
      .map((s) => financlopediaBySlug[s])
      .filter(Boolean);
  }, [currentCluster, entry]);

  // Learning path
  const learningPathEntries = useMemo(() => {
    if (!entry || !entry.learningPath) return [];
    return entry.learningPath
      .map((s) => financlopediaBySlug[s])
      .filter(Boolean);
  }, [entry]);

  if (!entry) {
    return <Navigate to={`/${lng}/financlopedia`} replace />;
  }

  // Article + FAQPage schema
  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: `${entry.term} - ${entry.termFull}`,
        description: entry.metaDescription,
        url: `${SITE_URL}/financlopedia/${entry.slug}`,
        dateModified: entry.lastUpdated,
        author: {
          '@type': 'Person',
          name: entry.author,
        },
        publisher: {
          '@type': 'Organization',
          name: 'FinMaple',
          url: SITE_URL,
          logo: { '@type': 'ImageObject', url: `${SITE_URL}/og-image.jpg` },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/financlopedia/${entry.slug}`,
        },
        about: {
          '@type': 'DefinedTerm',
          name: entry.term,
          description: entry.atomicAnswer,
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: entry.faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      },
    ],
  };

  const inlineCalc = calculatorRegistry[slug] || null;
  const InlineCalcComponent = inlineCalc?.component;

  const tocItems = entry.sections.map((s, i) => ({
    id: `section-${i}`,
    label: s.h2,
  }));

  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh' }}>
      <SEOHead
        title={`${entry.term} - ${entry.termFull}`}
        description={entry.metaDescription}
        canonical={`/financlopedia/${entry.slug}`}
        schema={articleSchema}
        noIndex={i18n.language !== 'es'}
      />

      <PageHeader
        title={entry.term}
        subtitle={entry.termFull}
        variant="minimal"
        breadcrumbs={[
          { label: l.breadcrumb, to: '/financlopedia' },
          { label: entry.term },
        ]}
        metadata={{
          author: entry.author,
          date: entry.lastUpdated,
          readTime: `${entry.readTime} ${l.readTime}`,
        }}
      >
        {entry.impact && <ImpactBadge impact={entry.impact} size="medium" />}
        <Chip
          label={l.categories[entry.category]}
          size="small"
          sx={{
            fontWeight: 500,
            bgcolor: 'transparent',
            color: '#5F6B7A',
            border: '1px solid #E5E7EB',
            height: 22,
            fontSize: '0.75rem',
          }}
        />
        <Chip
          label={l.difficulty[entry.difficulty] || entry.difficulty}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: 500,
            borderColor: '#E5E7EB',
            color: '#5F6B7A',
            height: 22,
            fontSize: '0.75rem',
          }}
        />
      </PageHeader>

      {/* Article container - editorial max-width */}
      <Container maxWidth="md" sx={{ pb: { xs: 4, md: 8 } }}>
        <Box sx={{ maxWidth: 760, mx: 'auto' }}>
          {/* Share buttons */}
          <Box sx={{ mb: 3 }}>
            <ShareButtons title={entry.term} />
          </Box>

          {/* Atomic Answer */}
          <AtomicAnswer text={entry.atomicAnswer} />

          {/* Key Takeaways - editorial block, not card */}
          <Box
            sx={{
              mb: 6,
              pl: { xs: 0, md: 0.5 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                mb: 2,
              }}
              onClick={() => setTakeawaysOpen(!takeawaysOpen)}
            >
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 600,
                  color: '#5F6B7A',
                  letterSpacing: 1.5,
                  fontSize: '0.75rem',
                }}
              >
                {l.keyTakeaways}
              </Typography>
              {takeawaysOpen ? (
                <ExpandLess sx={{ color: '#E6E9EE', fontSize: 20 }} />
              ) : (
                <ExpandMore sx={{ color: '#E6E9EE', fontSize: 20 }} />
              )}
            </Box>
            <Box sx={{ display: takeawaysOpen ? 'block' : 'none' }}>
              <Box component="ul" sx={{ pl: 0, mt: 0, mb: 0, listStyle: 'none' }}>
                {entry.keyTakeaways.map((item, i) => (
                  <Box
                    component="li"
                    key={i}
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      mb: 2.5,
                      alignItems: 'flex-start',
                    }}
                  >
                    <CheckCircleOutline
                      sx={{ color: '#E6E9EE', fontSize: 15, mt: 0.5, flexShrink: 0 }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: '#4B5563', lineHeight: 1.7 }}
                    >
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Table of Contents - inline, no card */}
          <Box
            sx={{
              borderTop: '1px solid #F7F9FC',
              borderBottom: '1px solid #F7F9FC',
              py: { xs: 2, md: 2.5 },
              mb: 6,
            }}
          >
            <Typography
              variant="overline"
              sx={{
                fontWeight: 600,
                color: '#5F6B7A',
                letterSpacing: 1.5,
                fontSize: '0.75rem',
                display: 'block',
                mb: 1.5,
              }}
            >
              {l.toc}
            </Typography>
            <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
              {tocItems.map((item) => (
                <Box component="li" key={item.id} sx={{ mb: 0.5, color: '#E6E9EE' }}>
                  <Link
                    href={`#${item.id}`}
                    underline="hover"
                    sx={{
                      color: '#5F6B7A',
                      fontWeight: 400,
                      fontSize: '0.875rem',
                    }}
                  >
                    {item.label}
                  </Link>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Introduction */}
          <Typography
            variant="body1"
            sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 6, fontSize: '1.0625rem' }}
          >
            {entry.introduction}
          </Typography>

          {/* Main Sections (H2s) */}
          {entry.sections.map((section, i) => (
            <Box key={i} id={`section-${i}`} sx={{ mb: 6, scrollMarginTop: '80px' }}>
              {/* Subtle divider before each H2 */}
              <Divider sx={{ mb: 5, borderColor: '#F7F9FC' }} />

              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 700,
                  color: '#1A1A1A',
                  mb: 3,
                  fontSize: { xs: '1.35rem', md: '1.55rem' },
                  lineHeight: 1.3,
                  letterSpacing: '-0.01em',
                }}
              >
                {section.h2}
              </Typography>
              <Box
                sx={{
                  '& p': {
                    color: '#1A1A1A',
                    lineHeight: 1.8,
                    mb: 2.5,
                    fontSize: '1rem',
                  },
                  '& ul, & ol': {
                    pl: 2.5,
                    mb: 2.5,
                    '& li': {
                      color: '#1A1A1A',
                      lineHeight: 1.8,
                      mb: 1,
                      fontSize: '1rem',
                    },
                  },
                  '& strong': { color: '#1A1A1A', fontWeight: 600 },
                  '& a': { color: '#1E3A5F', fontWeight: 500 },
                }}
                dangerouslySetInnerHTML={{
                  __html: autoLinkTerms(section.content, entry.slug, lng),
                }}
              />

              {/* Affiliate widget after section 2 */}
              {i === 1 && entry.affiliateWidget && (
                <Box
                  sx={{
                    borderTop: '1px solid #F7F9FC',
                    borderBottom: '1px solid #F7F9FC',
                    py: { xs: 2.5, md: 3 },
                    my: 5,
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                  }}
                >
                  <CompareArrows
                    sx={{
                      color: '#E6E9EE',
                      fontSize: 22,
                      flexShrink: 0,
                      display: { xs: 'none', sm: 'block' },
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: '#1A1A1A', mb: 0.25, fontSize: '0.875rem' }}
                    >
                      {entry.affiliateWidget.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#5F6B7A', lineHeight: 1.5, fontSize: '0.8125rem' }}
                    >
                      {entry.affiliateWidget.description}
                    </Typography>
                  </Box>
                  <Button
                    component={RouterLink}
                    to={`/${lng}${entry.affiliateWidget.ctaLink}`}
                    variant="outlined"
                    endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
                    sx={{
                      borderColor: '#E5E7EB',
                      color: '#5F6B7A',
                      textTransform: 'none',
                      fontWeight: 500,
                      borderRadius: '8px',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      fontSize: '0.8rem',
                      '&:hover': { bgcolor: '#F9FAFB', borderColor: '#5F6B7A' },
                    }}
                  >
                    {entry.affiliateWidget.ctaText}
                  </Button>
                </Box>
              )}

              {/* Inline calculator injection */}
              {inlineCalc && i === inlineCalc.afterSectionIndex && InlineCalcComponent && (
                <CalculatorShell title={inlineCalc.title}>
                  <InlineCalcComponent />
                </CalculatorShell>
              )}
            </Box>
          ))}

          {/* Related Terms */}
          {relatedEntries.length > 0 && (
            <Box sx={{ mb: 6 }}>
              <Divider sx={{ mb: 5, borderColor: '#F7F9FC' }} />
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 600,
                  color: '#5F6B7A',
                  letterSpacing: 1.5,
                  fontSize: '0.75rem',
                  display: 'block',
                  mb: 2.5,
                }}
              >
                {l.relatedTerms}
              </Typography>
              <Grid container spacing={2}>
                {relatedEntries.map((rel) => (
                  <Grid item xs={12} sm={6} key={rel.slug}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: '10px',
                        border: '1px solid #F7F9FC',
                        transition: 'border-color 0.2s ease',
                        '&:hover': {
                          borderColor: '#E6E9EE',
                        },
                      }}
                    >
                      <CardActionArea
                        component={RouterLink}
                        to={`/${lng}/financlopedia/${rel.slug}`}
                      >
                        <CardContent sx={{ p: 2.5 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, color: '#1A1A1A', fontSize: '0.875rem' }}
                              >
                                {rel.term}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: '#5F6B7A' }}
                              >
                                {rel.termFull}
                              </Typography>
                            </Box>
                            <ArrowForward
                              sx={{ color: '#E6E9EE', fontSize: 16 }}
                            />
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Otros términos en este cluster */}
          {clusterSiblings.length > 0 && currentCluster && (
            <Box sx={{ mb: 6 }}>
              <Divider sx={{ mb: 5, borderColor: '#F7F9FC' }} />
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 600,
                  color: '#5F6B7A',
                  letterSpacing: 1.5,
                  fontSize: '0.75rem',
                  display: 'block',
                  mb: 2.5,
                }}
              >
                {`${l.clusterPrefix} ${currentCluster.name}`}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  flexWrap: { xs: 'nowrap', md: 'wrap' },
                  overflowX: { xs: 'auto', md: 'visible' },
                  pb: { xs: 1, md: 0 },
                  '&::-webkit-scrollbar': { height: 4 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: '#E6E9EE', borderRadius: 2 },
                }}
              >
                {clusterSiblings.map((sib) => (
                  <Card
                    key={sib.slug}
                    elevation={0}
                    sx={{
                      borderRadius: '10px',
                      border: '1px solid #F7F9FC',
                      minWidth: { xs: 200, md: 'auto' },
                      width: { xs: 'auto', md: 'calc(50% - 8px)' },
                      flexShrink: 0,
                      transition: 'border-color 0.2s ease',
                      '&:hover': {
                        borderColor: currentCluster.color,
                      },
                    }}
                  >
                    <CardActionArea
                      component={RouterLink}
                      to={`/${lng}/financlopedia/${sib.slug}`}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, color: '#1A1A1A', fontSize: '0.8125rem' }}
                        >
                          {sib.term}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: '#5F6B7A', display: 'block', mt: 0.25 }}
                        >
                          {sib.termFull}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </Box>
            </Box>
          )}

          {/* Ruta recomendada */}
          {learningPathEntries.length > 0 && (
            <Box sx={{ mb: 6 }}>
              <Divider sx={{ mb: 5, borderColor: '#F7F9FC' }} />
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 600,
                  color: '#5F6B7A',
                  letterSpacing: 1.5,
                  fontSize: '0.75rem',
                  display: 'block',
                  mb: 0.5,
                }}
              >
                {l.learningPath}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: '#5F6B7A', fontSize: '0.8125rem', mb: 2.5 }}
              >
                {l.learningPathSub(entry.term)}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {learningPathEntries.map((step, i) => (
                  <Card
                    key={step.slug}
                    elevation={0}
                    sx={{
                      borderRadius: '10px',
                      border: '1px solid #F7F9FC',
                      transition: 'border-color 0.2s ease',
                      '&:hover': { borderColor: '#E6E9EE' },
                    }}
                  >
                    <CardActionArea
                      component={RouterLink}
                      to={`/${lng}/financlopedia/${step.slug}`}
                    >
                      <CardContent sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '8px',
                            bgcolor: '#F7F9FC',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1E3A5F' }}>
                            {i + 1}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, color: '#1A1A1A', fontSize: '0.8125rem' }}
                          >
                            {step.term}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: '#5F6B7A' }}
                          >
                            {step.termFull}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                          <AccessTime sx={{ fontSize: 12, color: '#E6E9EE' }} />
                          <Typography sx={{ fontSize: '12px', color: '#E6E9EE' }}>
                            {step.readTime} min
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </Box>
            </Box>
          )}

          {/* FAQs */}
          <FAQSection faqs={entry.faqs} title={l.faqTitle(entry.term)} />

          {/* Sources */}
          {entry.sources && entry.sources.length > 0 && (
            <Box sx={{ mb: 6 }}>
              <Divider sx={{ mb: 5, borderColor: '#F7F9FC' }} />
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 600,
                  color: '#5F6B7A',
                  letterSpacing: 1.5,
                  fontSize: '0.75rem',
                  display: 'block',
                  mb: 1.5,
                }}
              >
                {l.sources}
              </Typography>
              <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
                {entry.sources.map((source, i) => (
                  <Box component="li" key={i} sx={{ mb: 1, color: '#E5E7EB' }}>
                    <Link
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{
                        color: '#5F6B7A',
                        fontWeight: 400,
                        fontSize: '0.8125rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '&:hover': { color: '#5F6B7A' },
                      }}
                    >
                      {source.label}
                      <OpenInNew sx={{ fontSize: 12 }} />
                    </Link>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Feedback */}
          <FeedbackWidget contentId={entry.slug} />

          {/* Disclaimer */}
          <Divider sx={{ my: 5, borderColor: '#F7F9FC' }} />
          <Typography
            variant="body2"
            sx={{ color: '#E6E9EE', fontStyle: 'italic', lineHeight: 1.6, fontSize: '0.75rem' }}
          >
            {l.disclaimer}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default FinanclopediaEntryPage;
